import { useMemo } from 'react';

import { Table } from '@/components/table/Table';
import type { Column } from '@/components/table/types';
import type { GroupedRow } from '@/types';

interface GroupedResultsProps {
  groups: GroupedRow[];
  groupByFields: string[];
  aggregationFields?: string[];
  isFetching?: boolean;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
}

type GroupedTableRow = Record<string, unknown> & { _rowKey: string };

function formatFieldLabel(field: string): string {
  return field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function GroupedResults({
  groups,
  groupByFields,
  aggregationFields = [],
  isFetching,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
}: GroupedResultsProps) {
  const columns = useMemo((): Column<GroupedTableRow>[] => {
    const cols: Column<GroupedTableRow>[] = [];

    for (const field of groupByFields) {
      cols.push({
        id: field as keyof GroupedTableRow,
        label: formatFieldLabel(field),
        cell: (row: GroupedTableRow) => {
          const val = row[field];
          if (val === null || val === undefined) return '(empty)';
          return String(val);
        },
        minWidth: 120,
      });
    }

    cols.push({
      id: 'count' as keyof GroupedTableRow,
      label: 'Count',
      cell: (row: GroupedTableRow) =>
        (row.count as number)?.toLocaleString() ?? '0',
      fixedWidth: 100,
      align: 'right',
    });

    for (const aggField of aggregationFields) {
      cols.push({
        id: aggField as keyof GroupedTableRow,
        label: formatFieldLabel(aggField),
        cell: (row: GroupedTableRow) => {
          const val = row[aggField];
          if (val === null || val === undefined) return '-';
          if (typeof val === 'number') return val.toLocaleString();
          return String(val);
        },
        minWidth: 100,
        align: 'right',
      });
    }

    return cols;
  }, [groupByFields, aggregationFields]);

  const tableData = useMemo(
    () =>
      groups.map((group, index) => ({
        ...group.fields,
        count: group.count,
        _rowKey: `group-${index}`,
      })) as (GroupedTableRow & { children?: GroupedTableRow[] })[],
    [groups]
  );

  return (
    <div data-testid="query-grouped-results" className="h-full">
      <p className="text-chariot-text-secondary pb-4 pl-4 text-sm">
        {groups.length.toLocaleString()} group
        {groups.length !== 1 ? 's' : ''}
      </p>
      <Table
        name="query-grouped-results"
        columns={columns}
        data={tableData}
        status="success"
        error={null}
        isFetching={isFetching}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </div>
  );
}
